@if (singleOption()) {
  <!-- Just show a button as there is only 1 option - Only used for Monitoring and reports-->
  <p-button
    icon="pi pi-play-circle"
    [buttonProps]="{ autofocus: false }"
    label="Run"
    test-id="run-button"
    severity="success"
    [routerLink]="!singleOption().action ? singleOption().link : null"
    [queryParams]="!singleOption().action ? singleOption().linkQueryParams : null"
    (click)="singleOption().action ? singleOption().action() : null"
    [disabled]="singleOption().visibility === 'disabled'"
    [pTooltip]="singleOption().tooltip"
    tooltipPosition="left" />
} @else {
  <p-button
    icon="pi pi-play-circle"
    [buttonProps]="{ autofocus: false }"
    label="Run"
    (click)="runOptionsPopover.toggle($event)"
    test-id="run-button"
    severity="success" />
  <p-popover #runOptionsPopover>
    <div class="tw:flex tw:flex-col">
      @for (category of executionOptionCategories(); track category.name) {
        @if (category.options.length) {
          <div
            class="tw:flex tw:flex-col"
            role="group"
            [attr.aria-label]="category.name">
            <div class="tw:px-3 tw:pt-2 tw:pb-1 tw:text-sm tw:font-semibold tw:text-muted-color">
              {{ category.name }}
            </div>
            @for (option of category.options; track option.type) {
              <!-- Note: The button is wrapped in a <span> to ensure the tooltip works even when the button is disabled. -->
              <span
                [pTooltip]="
                  option.visibility === 'disabled'
                    ? option.tooltip || 'This option is not currently available'
                    : option.tooltip
                "
                tooltipPosition="left">
                <a
                  pButton
                  [attr.test-id]="'run-button-option-' + option.name"
                  class="tw:w-52 tw:justify-start"
                  [class.p-disabled]="option.visibility === 'disabled'"
                  [icon]="option.icon"
                  [label]="option.name"
                  severity="contrast"
                  text
                  [routerLink]="
                    option.visibility !== 'disabled' && !option.action ? option.link : null
                  "
                  [queryParams]="option.linkQueryParams ?? null"
                  (click)="option.action ? option.action() : null"></a>
              </span>
            }
          </div>
        }
      }
    </div>
  </p-popover>
}

<!-- Hillclimb Dialog -->
<cor-dialog-box
  [(visible)]="hillClimbing.showDialog"
  [style]="{ width: 'auto', minWidth: 'unset', maxWidth: '40vw' }"
  title="Hillclimb Configuration"
  (onHide)="onCloseHillClimbingDialog()">
  @if (hillClimbing.showDialog()) {
    @if (hillClimbing.step() === 'options') {
      <ng-container [ngTemplateOutlet]="hillclimb_options" />
    }
    @if (hillClimbing.step() === 'noBaseJob') {
      <ng-container [ngTemplateOutlet]="hillclimb_no_base_job" />
    }
    @if (hillClimbing.step() === 'selectBaseJob') {
      <ng-container [ngTemplateOutlet]="hillclimb_select_base_job" />
    }
  }
</cor-dialog-box>

<!-- Hillclimb: Options('Has base job / No base job') -->
<ng-template #hillclimb_options>
  <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
    <i
      class="pi pi-info-circle tw:text-primary tw:mt-2"
      style="font-size: 3.5rem"></i>
    <div class="tw:font-bold tw:text-muted-color-emphasis tw:text-xl tw:text-center">
      Do you have a base job?
    </div>
    <div class="tw:text-muted-color-emphasis tw:text-center tw:px-2">
      Hillclimb optimization requires a base job to build upon
    </div>
    <div class="tw:flex tw:gap-2 tw:mt-2 tw:w-full">
      <div style="flex: 1 1 0">
        <p-button
          test-id="execution-options-no"
          fluid
          [buttonProps]="{ autofocus: false }"
          label="No"
          styleClass="tw:w-full"
          severity="secondary"
          (onClick)="hillClimbing.step.set('noBaseJob')" />
      </div>
      <div style="flex: 1 1 0">
        <p-button
          test-id="execution-options-yes"
          fluid
          [buttonProps]="{ autofocus: false }"
          styleClass="tw:w-full"
          label="Yes"
          (onClick)="hillClimbing.step.set('selectBaseJob')" />
      </div>
    </div>
  </div>
</ng-template>

<!-- Comparison Dialog -->
<cor-dialog-box
  [(visible)]="comparison.showDialog"
  [style]="{ width: 'auto', minWidth: 'unset', maxWidth: '40vw' }"
  title="Comparison Configuration"
  (onHide)="onCloseComparisonDialog()">
  @if (comparison.showDialog()) {
    @if (comparison.step() === 'options') {
      <ng-container [ngTemplateOutlet]="comparison_options" />
    }
    @if (comparison.step() === 'noComparisonJobs') {
      <ng-container [ngTemplateOutlet]="comparison_no_jobs" />
    }
    @if (comparison.step() === 'selectComparisonJobs') {
      <ng-container [ngTemplateOutlet]="comparison_select_jobs" />
    }
  }
</cor-dialog-box>

<!-- Comparison: Options ('Have previous jobs / No jobs') -->
<ng-template #comparison_options>
  <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
    <i
      class="pi pi-info-circle tw:text-primary tw:mt-2"
      style="font-size: 3.5rem"></i>
    <div class="tw:font-bold tw:text-muted-color-emphasis tw:text-xl tw:text-center">
      Do you have 2 completed jobs to compare?
    </div>
    <div class="tw:text-muted-color-emphasis tw:text-center tw:px-2">
      Comparison requires existing jobs to analyze their results side by side
    </div>
    <div class="tw:flex tw:gap-2 tw:mt-2 tw:w-full">
      <div style="flex: 1 1 0">
        <p-button
          test-id="execution-options-no-2"
          fluid
          [buttonProps]="{ autofocus: false }"
          label="No"
          styleClass="tw:w-full"
          severity="secondary"
          (onClick)="comparison.step.set('noComparisonJobs')" />
      </div>
      <div style="flex: 1 1 0">
        <p-button
          test-id="execution-options-yes-2"
          fluid
          [buttonProps]="{ autofocus: false }"
          styleClass="tw:w-full"
          label="Yes"
          (onClick)="comparison.step.set('selectComparisonJobs')" />
      </div>
    </div>
  </div>
</ng-template>

<!-- Comparison: No jobs template -->
<ng-template #comparison_no_jobs>
  <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
    <i
      class="pi pi-info-circle icon-danger tw:mt-2"
      style="font-size: 3.5rem"></i>
    <div class="tw:font-bold tw:text-muted-color-emphasis tw:text-xl tw:text-center">
      Jobs Are Required For Comparison
    </div>
    <div
      class="tw:border tw:border-surface tw:p-3 tw:rounded-lg tw:bg-emphasis tw:mt-1 tw:text-center">
      Run at least 2 jobs to be able to compare their results.
      <br />
      Once the jobs have completed, you can select them for comparison.
    </div>
    <div class="tw:flex tw:gap-2 tw:mt-2 tw:w-full tw:justify-end">
      <p-button
        test-id="execution-options-cancel"
        fluid
        [buttonProps]="{ autofocus: false }"
        label="Cancel"
        styleClass="tw:w-full"
        severity="secondary"
        (click)="onCloseComparisonDialog()" />
      <p-button
        test-id="execution-options-run-a-job"
        fluid
        [buttonProps]="{ autofocus: false }"
        styleClass="tw:w-full"
        icon="pi pi-play-circle"
        severity="success"
        label="Run a Job"
        [routerLink]="comparison.link()"
        [queryParams]="{
          type: EXECUTION_TYPE.SIMULATION,
          entityId: id(),
          object: object(),
        }" />
    </div>
  </div>
</ng-template>

<!-- Comparison: Select jobs template -->
<ng-template #comparison_select_jobs>
  <div class="tw:flex tw:flex-col tw:gap-3">
    <div class="tw:text-muted-color-emphasis">
      Choose the two completed jobs you want to compare
    </div>

    <div class="tw:flex tw:flex-col tw:gap-3">
      <div class="tw:flex tw:flex-col tw:gap-2">
        <small class="tw:font-bold tw:text-muted-color-emphasis">Left Job</small>
        <cor-input-select
          test-id="execution-options-select-left-job"
          [(ngModel)]="comparison.leftJobId"
          placeholder="Select left job"
          [searchKeys]="['alias', 'name']"
          [params]="{
            case: INPUT_CASES.REUSE_SIMULATIONS,
            entityType: object(),
            sourceEntityId: id(),
            sourceEntityType: object(),
            jobStatus: [SCHEDULED_JOB_STATUS.COMPLETED],
            projectSlug: currentProject(),
          }"
          displayExpr="name"
          valueExpr="id"
          searchExpr="name"
          searchMode="contains"
          mode="single" />
      </div>

      <div class="tw:flex tw:flex-col tw:gap-2">
        <small class="tw:font-bold tw:text-muted-color-emphasis">Right Job</small>
        <cor-input-select
          test-id="execution-options-select-right-job"
          [(ngModel)]="comparison.rightJobId"
          placeholder="Select right job"
          [searchKeys]="['alias', 'name']"
          [params]="{
            case: INPUT_CASES.REUSE_SIMULATIONS,
            entityType: object(),
            sourceEntityId: id(),
            sourceEntityType: object(),
            jobStatus: [SCHEDULED_JOB_STATUS.COMPLETED],
            projectSlug: currentProject(),
          }"
          displayExpr="name"
          valueExpr="id"
          searchExpr="name"
          searchMode="contains"
          mode="single" />
      </div>
    </div>

    @if (comparison.leftJobId() && comparison.rightJobId()) {
      <p-message
        severity="info"
        variant="simple"
        icon="pi pi-info-circle"
        text="These jobs will be used as the left and right jobs for comparison"
        [motionOptions]="{ disabled: true }"
        class="tw:mt-1" />
    }

    <div class="tw:flex tw:gap-2 tw:mt-3 tw:w-full tw:justify-end">
      <p-button
        test-id="execution-options-cancel-2"
        fluid
        [buttonProps]="{ autofocus: false }"
        label="Cancel"
        styleClass="tw:w-full"
        severity="secondary"
        (click)="onCloseComparisonDialog()" />
      <p-button
        test-id="execution-options-start-comparison"
        fluid
        [buttonProps]="{ autofocus: false }"
        [disabled]="
          !comparison.leftJobId() ||
          !comparison.rightJobId() ||
          comparison.leftJobId() === comparison.rightJobId()
        "
        styleClass="tw:w-full"
        icon="pi pi-columns"
        severity="success"
        label="Start Comparison"
        [routerLink]="comparison.link()"
        [queryParams]="{
          comparisonId: comparison.rightJobId(),
        }" />
    </div>
  </div>
</ng-template>

<!-- Hillclimb: No base job template -->
<ng-template #hillclimb_no_base_job>
  <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
    <p-message
      severity="error"
      variant="simple"
      [motionOptions]="{ disabled: true }"
      class="tw:w-full">
      <span class="tw:font-bold tw:block tw:mb-1">Base Job Is Required</span>
      Start by running a base job to establish your initial prompt and results. From there, the
      hillclimb process iteratively tweaks and evaluates variations of the prompt, optimizing toward
      better outputs.
    </p-message>
    <div class="tw:flex tw:gap-2 tw:mt-2 tw:w-full tw:justify-end">
      <p-button
        test-id="execution-options-cancel-3"
        fluid
        [buttonProps]="{ autofocus: false }"
        label="Cancel"
        styleClass="tw:w-full"
        severity="secondary"
        (click)="onCloseHillClimbingDialog()" />
      <p-button
        test-id="execution-options-run-base-job"
        fluid
        [buttonProps]="{ autofocus: false }"
        styleClass="tw:w-full"
        icon="pi pi-play-circle"
        severity="success"
        label="Run base Job"
        [routerLink]="hillClimbing.link()"
        [queryParams]="{
          type: EXECUTION_TYPE.SIMULATION,
          entityId: id(),
          object: object(),
        }" />
    </div>
  </div>
</ng-template>

<!-- Hillclimb: Select base job template -->
<ng-template #hillclimb_select_base_job>
  <div class="tw:flex tw:flex-col tw:gap-3">
    <div class="tw:text-muted-color-emphasis">
      Choose the completed job you want to use as the starting point for Hillclimbing
    </div>

    <cor-input-select
      test-id="execution-options-select-a-base-job"
      [(ngModel)]="hillClimbing.baseJobId"
      placeholder="Select a base job"
      [searchKeys]="['alias', 'name']"
      [params]="{
        case: INPUT_CASES.REUSE_SIMULATIONS,
        entityType: object(),
        sourceEntityId: id(),
        sourceEntityType: object(),
        jobStatus: [SCHEDULED_JOB_STATUS.COMPLETED],
        projectSlug: currentProject(),
      }"
      displayExpr="name"
      valueExpr="id"
      searchExpr="name"
      searchMode="contains"
      mode="single" />
    @if (hillClimbing.baseJobId()) {
      <p-message
        severity="success"
        variant="simple"
        text="This job will be used as the starting point for Hillclimbing"
        [motionOptions]="{ disabled: true }"
        class="tw:mt-1" />
    }
    <div class="tw:flex tw:gap-2 tw:mt-3 tw:w-full tw:justify-end">
      <p-button
        test-id="execution-options-cancel-4"
        fluid
        [buttonProps]="{ autofocus: false }"
        label="Cancel"
        styleClass="tw:w-full"
        severity="secondary"
        (click)="onCloseHillClimbingDialog()" />
      <p-button
        test-id="execution-options-start-hillclimb"
        fluid
        [buttonProps]="{ autofocus: false }"
        [disabled]="!hillClimbing.baseJobId()"
        styleClass="tw:w-full"
        icon="pi pi-chart-line"
        severity="success"
        label="Start Hillclimb"
        [routerLink]="hillClimbing.link()"
        [queryParams]="{
          type: EXECUTION_TYPE.HILL_CLIMBING,
          entityId: id(),
          object: object(),
          jobId: hillClimbing.baseJobId(),
          baseHillclimbSimId: hillClimbing.baseJobId(),
        }" />
    </div>
  </div>
</ng-template>

<!-- Report Execution Dialog -->
<cor-dialog-box
  [(visible)]="reportExecution.showDialog"
  [style]="{ width: 'auto', minWidth: 'unset' }"
  title="Report Execution"
  (onHide)="onCloseHillClimbingDialog()">
  @if (reportExecution.showDialog()) {
    <div class="tw:flex tw:flex-col tw:gap-2">
      <div class="tw:text-muted-color-emphasis">Choose how you want to run this report</div>
      <div
        class="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:border tw:border-surface tw:rounded-2xl tw:p-3 tw:mt-2">
        <i class="pi pi-wave-pulse tw:text-xl"></i>
        <div class="tw:flex-auto">
          <div
            class="tw:font-bold tw:text-muted-color-emphasis"
            style="font-size: 1.1rem">
            Test Report Logic
          </div>
          <small>Use example data to test and validate your report logic and configurations</small>
        </div>

        <p-button
          test-id="execution-options-run"
          [buttonProps]="{ autofocus: false }"
          icon="pi pi-play-circle"
          severity="success"
          label="Run"
          size="small"
          [routerLink]="reportExecution.link()"
          [queryParams]="{
            type: EXECUTION_TYPE.SIMULATION,
            entityId: this.id(),
            object: this.object(),
          }" />
      </div>
      <div class="tw:flex tw:flex-col tw:border tw:border-surface tw:rounded-2xl">
        <div class="tw:flex tw:items-center tw:gap-3 tw:p-3">
          <i class="pi pi-box tw:text-xl"></i>
          <div class="tw:flex-auto">
            <div
              class="tw:font-bold tw:text-muted-color-emphasis"
              style="font-size: 1.1rem">
              Run on Registered Object
            </div>
            <small>Run the report on a specific registered object like a Pipeline or a Model</small>
          </div>
          <!-- NOTE: Currently we are only supporting running report for simulation from here -->
          <p-button
            test-id="execution-options-run-2"
            [buttonProps]="{ autofocus: false }"
            icon="pi pi-play-circle"
            severity="success"
            label="Run"
            size="small"
            [disabled]="!reportExecution.selectedItem()"
            [pTooltip]="
              !reportExecution.selectedItem() &&
              'Please select the registered object first to run the report'
            "
            [routerLink]="reportExecutionlink()"
            [queryParams]="{
              type: EXECUTION_TYPE.SIMULATION,
              entityId: reportExecution.selectedItem(),
              object: reportExecution.selectedObject(),
              reportId: this.id(),
            }" />
        </div>

        <div class="tw:flex tw:flex-col tw:gap-2 tw:border-t tw:border-surface tw:p-3">
          <small>Select the object you want to run this report on</small>

          <div class="row tw:p-0! tw:m-0">
            <p-select
              test-id="execution-options-please-select-an-object"
              [class]="
                reportExecution.selectedObject() ? 'col-4 tw:rounded-none tw:rounded-s' : 'col-12'
              "
              appendTo="body"
              [options]="reportExecution.objects()"
              [(ngModel)]="reportExecution.selectedObject"
              optionDisabled="disabled"
              (onChange)="onObjectChange($event.value)"
              placeholder="Please select an object"
              optionValue="id"
              filterBy="name">
              <ng-template
                let-object
                pTemplate="selectedItem">
                <div class="tw:flex tw:gap-2 tw:items-center">
                  <i
                    [class]="object.icon"
                    style="font-size: 1rem"></i>
                  <span class="tw:truncate">{{ object.name }}</span>
                </div>
              </ng-template>
              <ng-template
                let-object
                pTemplate="item">
                <div class="tw:flex tw:gap-2 tw:items-center">
                  <i
                    [class]="object.icon"
                    style="font-size: 1rem"></i>
                  <span>{{ object.name }}</span>
                </div>
              </ng-template>
            </p-select>
            @if (reportExecution.selectedObject()) {
              <p-select
                test-id="execution-options-select-an-item"
                class="col-8 tw:rounded-none tw:rounded-e"
                appendTo="body"
                [options]="reportExecution.objectItemsList()"
                [(ngModel)]="reportExecution.selectedItem"
                [filter]="true"
                filterBy="name"
                optionLabel="name"
                optionValue="id"
                [placeholder]="reportExecution.loadingItems() ? 'Loading...' : 'Select an item'" />
            }
          </div>

          @if (reportExecution.selectedItem()) {
            <p-message
              severity="success"
              variant="simple"
              [motionOptions]="{ disabled: true }"
              class="tw:mt-1">
              {{
                `This report will be run on the ${OBJECT_DISPLAY_NAMES[reportExecution.selectedObject()]} - "${
                  (
                    reportExecution.objectItemsList()
                    | findItem: ['id', reportExecution.selectedItem()]
                  )?.name
                }"`
              }}
            </p-message>
          }
        </div>
      </div>
    </div>
  }
</cor-dialog-box>
